import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Calculator, Save, Edit2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ErrorModal from '@/components/ErrorModal';

const FLAG_URL = (code) => `https://flagcdn.com/w40/${code?.toLowerCase()}.png`;

export default function Results() {
  const [matches, setMatches] = useState([]);
  const [stages, setStages] = useState([]);
  const [results, setResults] = useState({});
  const [existingResults, setExistingResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [editingResult, setEditingResult] = useState({ home_goals: '', away_goals: '' });

  // Filtros
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStage, setFilterStage] = useState('all');

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => {
    Promise.all([
      api.get('/matches/enriched'),
      api.get('/results'),
      api.get('/stages'),
    ]).then(([m, r, s]) => {
      setMatches(m.data);
      setStages(s.data);
      const existing = {};
      r.data.forEach(res => { existing[res.match_id] = { home_goals: res.home_goals, away_goals: res.away_goals }; });
      setExistingResults(existing);
      setResults(existing);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateResult = (matchId, field, value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0) return;
    setResults(prev => ({ ...prev, [matchId]: { ...prev[matchId], [field]: num } }));
  };

  const saveResult = async (matchId) => {
    const r = results[matchId];
    if (r?.home_goals === undefined || r?.away_goals === undefined) {
      setErrorModal({
        open: true,
        title: 'Campos requeridos',
        message: 'Por favor, ingresa ambos marcadores (goles del Equipo 1 y Equipo 2).'
      });
      return;
    }
    try {
      await api.post('/results', { match_id: matchId, home_goals: r.home_goals, away_goals: r.away_goals });
      toast.success('Resultado guardado');
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar resultado';
      setErrorModal({
        open: true,
        title: 'Error al guardar resultado',
        message: errorMessage
      });
    }
  };

  const calculatePoints = async () => {
    setCalculating(true);
    try {
      const res = await api.post('/results/calculate');
      toast.success(res.data.message);
    } catch (err) { toast.error('Error al calcular puntos'); }
    finally { setCalculating(false); }
  };

  const openMatchDetails = (match) => {
    const result = existingResults[match.id];
    setSelectedMatch(match);
    setEditingResult({
      home_goals: result?.home_goals?.toString() || '',
      away_goals: result?.away_goals?.toString() || ''
    });
  };

  const closeMatchDetails = () => {
    setSelectedMatch(null);
    setEditingResult({ home_goals: '', away_goals: '' });
  };

  const saveMatchResult = async () => {
    if (!selectedMatch) return;
    const homeGoals = parseInt(editingResult.home_goals);
    const awayGoals = parseInt(editingResult.away_goals);
    
    if (isNaN(homeGoals) || isNaN(awayGoals) || homeGoals < 0 || awayGoals < 0) {
      setErrorModal({
        open: true,
        title: 'Marcadores inválidos',
        message: 'Por favor, ingresa marcadores válidos (números mayores o iguales a 0).'
      });
      return;
    }

    try {
      await api.post('/results', { 
        match_id: selectedMatch.id, 
        home_goals: homeGoals, 
        away_goals: awayGoals 
      });
      toast.success('Marcador actualizado');
      closeMatchDetails();
      load();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al guardar marcador';
      setErrorModal({
        open: true,
        title: 'Error al guardar marcador',
        message: errorMessage
      });
    }
  };

  // Filtrar matches según los selects del usuario
  const matchesFilteredByStage = filterStage === 'all'
    ? matches
    : matches.filter(m => m.stage?.id === filterStage);

  const showPending = filterStatus === 'all' || filterStatus === 'pendiente';
  const showFinalized = filterStatus === 'all' || filterStatus === 'finalizado';

  const pendingMatches = showPending ? matchesFilteredByStage.filter(m => m.status !== 'finalizado') : [];
  const finalizedMatches = showFinalized ? matchesFilteredByStage.filter(m => m.status === 'finalizado') : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-results-page">
      <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Marcadores y Resultados</h2>

      <button onClick={calculatePoints} disabled={calculating} data-testid="calculate-points-button"
        className="w-full border-2 border-[#00FF87]/40 text-[#00FF87] py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#00FF87]/10 transition-colors">
        <Calculator size={18} /> {calculating ? 'Calculando...' : 'CALCULAR PUNTOS'}
      </button>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Estado del partido</label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="results-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todos los partidos</SelectItem>
              <SelectItem value="pendiente" className="text-white">Pendientes por jugar</SelectItem>
              <SelectItem value="finalizado" className="text-white">Finalizados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Etapa</label>
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="results-filter-stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todas las etapas</SelectItem>
              {stages.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending matches - set results */}
      {pendingMatches.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-[#F4E39E] mb-3" style={{ fontFamily: 'Montserrat' }}>Partidos Pendientes</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
            {pendingMatches.map((m) => (
              <div key={m.id} className="glass-card p-4" data-testid={`result-pending-${m.id}`}>
                <div className="text-xs text-[#F4E39E] mb-2">{m.stage?.name}</div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_URL(m.home_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold text-sm">{m.home_team?.code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" value={results[m.id]?.home_goals ?? ''} onChange={e => updateResult(m.id, 'home_goals', e.target.value)}
                      className="w-12 h-10 bg-[#0A0F1C] border border-[#F4E39E]/30 rounded-lg text-white font-bold text-center focus:border-[#F4E39E] outline-none" />
                    <span className="text-white/40">-</span>
                    <input type="number" min="0" value={results[m.id]?.away_goals ?? ''} onChange={e => updateResult(m.id, 'away_goals', e.target.value)}
                      className="w-12 h-10 bg-[#0A0F1C] border border-[#F4E39E]/30 rounded-lg text-white font-bold text-center focus:border-[#F4E39E] outline-none" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{m.away_team?.code}</span>
                    <img src={FLAG_URL(m.away_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                  </div>
                </div>
                <button onClick={() => saveResult(m.id)} className="w-full gold-btn py-2 rounded-lg text-sm flex items-center justify-center gap-1">
                  <Save size={14} /> Guardar Marcador
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Finalized */}
      {finalizedMatches.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white/60 mb-3" style={{ fontFamily: 'Montserrat' }}>Marcadores Finales</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 lg:gap-3">
            {finalizedMatches.map((m) => (
              <div
                key={m.id}
                className="glass-card-subtle p-4 cursor-pointer hover:bg-white/5 transition-colors"
                data-testid={`result-final-${m.id}`}
                onClick={() => openMatchDetails(m)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#F4E39E] text-xs font-semibold">{m.stage?.name}</span>
                  <span className="text-white/40 text-[10px]">{m.date} · {m.time}</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <img src={FLAG_URL(m.home_team?.flag_code)} alt="" className="w-6 h-4 object-cover rounded shrink-0" onError={(e) => e.target.style.display='none'} />
                    <div className="min-w-0">
                      <div className="text-white/90 font-semibold text-sm truncate">{m.home_team?.name}</div>
                      <div className="text-white/40 text-[10px]">{m.home_team?.code}</div>
                    </div>
                  </div>
                  <div className="text-center shrink-0 px-2">
                    <span className="text-white font-bold text-lg">{existingResults[m.id]?.home_goals ?? '?'}</span>
                    <span className="text-white/30 mx-1">-</span>
                    <span className="text-white font-bold text-lg">{existingResults[m.id]?.away_goals ?? '?'}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <div className="min-w-0 text-right">
                      <div className="text-white/90 font-semibold text-sm truncate">{m.away_team?.name}</div>
                      <div className="text-white/40 text-[10px]">{m.away_team?.code}</div>
                    </div>
                    <img src={FLAG_URL(m.away_team?.flag_code)} alt="" className="w-6 h-4 object-cover rounded shrink-0" onError={(e) => e.target.style.display='none'} />
                  </div>
                </div>

                {(m.venue?.city || m.stadium?.name) && (
                  <div className="text-white/40 text-[10px] text-center mt-2 truncate">
                    {m.stadium?.name ? `${m.stadium.name} · ` : ''}{m.venue?.city}{m.venue?.country ? `, ${m.venue.country}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-center text-white/50 py-10">Cargando...</div>}

      {!loading && pendingMatches.length === 0 && finalizedMatches.length === 0 && (
        <div className="glass-card p-8 text-center text-white/40" data-testid="results-empty">
          No hay partidos que coincidan con los filtros seleccionados.
        </div>
      )}

      {/* Modal de detalles del partido */}
      <Dialog open={!!selectedMatch} onOpenChange={closeMatchDetails}>
        <DialogContent className="bg-[#111A30] border-[#926F39]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>
              Detalles del Partido
            </DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-4">
              {/* Información del partido */}
              <div className="bg-[#0A0F1C]/50 p-4 rounded-lg space-y-2">
                <div className="text-white/50 text-xs">Etapa</div>
                <div className="text-white font-medium">{selectedMatch.stage?.name}</div>
                
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-white/50 text-xs">Fecha</div>
                    <div className="text-white">{selectedMatch.date}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">Hora</div>
                    <div className="text-white">{selectedMatch.time}</div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <div className="text-white/50 text-xs">Sede</div>
                  <div className="text-white">{selectedMatch.venue?.city || selectedMatch.stadium?.name}</div>
                </div>
              </div>

              {/* Equipos y marcadores */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#0A0F1C]/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_URL(selectedMatch.home_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold">{selectedMatch.home_team?.code}</span>
                  </div>
                  <input 
                    type="number" 
                    min="0" 
                    value={editingResult.home_goals}
                    onChange={(e) => setEditingResult({...editingResult, home_goals: e.target.value})}
                    className="w-16 h-12 bg-[#0A0F1C] border-2 border-[#926F39]/40 rounded-lg text-white font-bold text-center text-xl focus:border-[#F4E39E] outline-none"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-[#0A0F1C]/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_URL(selectedMatch.away_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold">{selectedMatch.away_team?.code}</span>
                  </div>
                  <input 
                    type="number" 
                    min="0" 
                    value={editingResult.away_goals}
                    onChange={(e) => setEditingResult({...editingResult, away_goals: e.target.value})}
                    className="w-16 h-12 bg-[#0A0F1C] border-2 border-[#926F39]/40 rounded-lg text-white font-bold text-center text-xl focus:border-[#F4E39E] outline-none"
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={closeMatchDetails}
                  className="flex-1 py-2 px-4 rounded-lg border-2 border-[#926F39]/50 text-[#F4E39E] hover:bg-[#926F39]/20 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button 
                  onClick={saveMatchResult}
                  className="flex-1 py-2 px-4 rounded-lg gold-gradient text-[#0A0F1C] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <Save size={16} />
                  Guardar
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
